import React, { useEffect, useMemo } from "react";
import { Button, Divider, Tag } from "antd";
import { Check, MoreOutlined, Warning } from "assets/icons";
import { IDocumentType } from "store/lobby/lobby-model";
import { IConfigurationActive } from "store/location/location-model";
import theme from "styles/theme";
import useTranslation from "utils/hooks/useTranslation";
import * as S from "./styled/index.styled";

export interface IPropsScreen {
  hasAttendanceActive: boolean;
  expirationTimeActive: number;
  checkinActive: boolean;
  requestVehicleInboundActionActive: boolean;
  requestDockEntryActionActive: boolean;
  hasFinishOperationActionActive: boolean;
  releaseVehicleActionActive: boolean;
  vehicleArrivalConfirmActionActive: boolean;
  hasReleaseWMSDocumentsActive: boolean;
  hasLocationOutOfRangeActive: boolean;
  hasCheckinScheduleActive: boolean;
  documentTypeActive: IDocumentType;
  dropHookTypeActive?: number;
  dropHookActive: boolean;
  hasEntryPermissionActive: boolean;
  hasRfidReadingActive: boolean;
  plateFormatType: number;
}
interface IDispatchProps {
  locationsCheckinActive: (...args) => void;
  locationsHasAttendanceActive: (...args) => void;
  locationsExpirationTimeActive: (...args) => void;
  locationsAllowRequestVehicleInboundActionActive: (...args) => void;
  locationsAllowRequestDockEntryActionActive: (...args) => void;
  locationsHasFinishOperationActionActive: (...args) => void;
  locationsReleaseVehicleActionActive: (...args) => void;
  locationsAllowVehicleArrivalConfirmActionActive: (...args) => void;
  locationsHasReleaseWMSDocumentsActionActive: (...args) => void;
  locationsHasLocationOutOfRangeActive: (...args) => void;
  locationsHasCheckinScheduleActive: (...args) => void;
  locationsConfigurationsShowDrawer: () => void;
  locationsConfigurationsUpdateConfigurationActive: (...args) => void;
  locationsDocumentNumberActive: (...args) => void;
  locationsDropHookActive: (...args) => void;
  locationsHasEntryPermissionActive: (...args) => void;
  locationsHasRfidReadingActive: (...args) => void;
  locationsPlateFormatActive: (...args) => void;
}

export type IPropsConfiguration = IPropsScreen & IDispatchProps;

const ConfigurationTabPane = ({
  locationsCheckinActive,
  locationsHasAttendanceActive,
  locationsExpirationTimeActive,
  locationsAllowRequestVehicleInboundActionActive,
  locationsAllowRequestDockEntryActionActive,
  locationsHasFinishOperationActionActive,
  locationsReleaseVehicleActionActive,
  locationsAllowVehicleArrivalConfirmActionActive,
  locationsHasReleaseWMSDocumentsActionActive,
  locationsHasLocationOutOfRangeActive,
  locationsHasCheckinScheduleActive,
  locationsConfigurationsShowDrawer,
  locationsConfigurationsUpdateConfigurationActive,
  locationsDocumentNumberActive,
  locationsDropHookActive,
  locationsHasEntryPermissionActive,
  locationsHasRfidReadingActive,
  locationsPlateFormatActive,
  hasReleaseWMSDocumentsActive,
  hasAttendanceActive,
  expirationTimeActive,
  checkinActive,
  requestVehicleInboundActionActive,
  requestDockEntryActionActive,
  hasFinishOperationActionActive,
  releaseVehicleActionActive,
  vehicleArrivalConfirmActionActive,
  hasLocationOutOfRangeActive,
  hasCheckinScheduleActive,
  documentTypeActive,
  dropHookTypeActive,
  dropHookActive,
  hasEntryPermissionActive,
  hasRfidReadingActive,
  plateFormatType,
}: IPropsConfiguration): JSX.Element => {
  const ACTIVE = useTranslation({ id: "active" });
  const INACTIVE = useTranslation({ id: "inactive" });
  const SCHEDULING_TITLE = useTranslation({ id: "scheduling" });
  const SCHEDULING_DESCRIPTION = useTranslation({ id: "enable_scheduling_column_in_attendance_page_description" });
  const ATTENDANCE_TITLE = useTranslation({ id: "attendance" });
  const ATTENDANCE_DESCRIPTION = useTranslation({ id: "enable_menu_attendance_page_description" });
  const CALL_TO_LOBBY_TITLE = useTranslation({ id: "call_to_lobby" });
  const CALL_TO_LOBBY_DESCRIPTION = useTranslation({ id: "call_driver_to_lobby_description" });
  const CALL_TO_DOCK_TITLE = useTranslation({ id: "call_to_dock" });
  const CALL_TO_DOCK_DESCRIPTION = useTranslation({ id: "call_driver_to_dock_description" });
  const ALLOW_VEHICLE_ARRIVAL_CONFIRM_TITLE = useTranslation({ id: "allow_vehicle_arrival_confirm" });
  const RELEASE_WMS_DOCUMENTS_TITLE = useTranslation({ id: "release_wms_documents" });
  const RELEASE_WMS_DOCUMENTS_DESCRIPTION = useTranslation({ id: "enable_wms_load_start_description" });
  const EXTERNAL_DROP_HOOK_TITLE = useTranslation({ id: "enable_drop_and_hook_process" });
  const EXTERNAL_DROP_HOOK_DESCRIPTION = useTranslation({ id: "external_drop_hook_process_description" });
  const FINISH_OPERATION_TITLE = useTranslation({ id: "finish_operation" });
  const FINISH_OPERATION_DESCRIPTION = useTranslation({ id: "finalize_vehicle_loading_description" });
  const LOCATION_OUT_OF_RANGE_TITLE = useTranslation({ id: "has_location_out_of_range" });
  const LOCATION_OUT_OF_RANGE_DESCRIPTION = useTranslation({ id: "enable_track_and_trace_out_of_range_description" });
  const RELEASE_VEHICLE_TITLE = useTranslation({ id: "release_vehicle" });
  const RELEASE_VEHICLE_DESCRIPTION = useTranslation({ id: "sap_document_printing_description" });
  const EXPIRATION_TIME_TITLE = useTranslation({ id: "check_in_expiration_time" });
  const EXPIRATION_TIME_DESCRIPTION = useTranslation({ id: "expiration_time_description" });
  const VEHICLE_IN_THE_EXTERNAL_YARD_TITLE = useTranslation({ id: "vehicles_in_the_external_yard" });
  const VEHICLE_IN_THE_EXTERNAL_YARD_DESCRIPTION = useTranslation({ id: "vehicles_in_the_external_yard_description" });
  const DRIVER_DOCUMENT_TITLE = useTranslation({ id: "driver_document" });
  const DRIVER_DOCUMENT_DESCRIPTION = useTranslation({ id: "driver_document_description" });
  const COOL_FLEET_DESCRIPTION = useTranslation({ id: "cool_fleet_integration_description" });
  const COOL_FLEET_TITLE = useTranslation({ id: "cool_fleet_integration_title" });
  const RFID_READING_DESCRIPTION = useTranslation({ id: "antennas_and_tag_reading_description" });
  const RFID_READING_TITLE = useTranslation({ id: "antennas_and_tag_reading" });
  const PLATE_FORMAT_DESCRIPTION = useTranslation({ id: "plate_format" });

  const configurationsWithTranslations = useMemo(() => {
    const optionsConfigurations: IConfigurationActive[] = [
      {
        id: 1,
        description: SCHEDULING_DESCRIPTION,
        configuration: hasCheckinScheduleActive,
        descriptionName: SCHEDULING_TITLE,
        configurationName: "hasCheckinScheduleActive",
        isCheckinTime: false,
      },
      {
        id: 2,
        description: RFID_READING_DESCRIPTION,
        configuration: hasRfidReadingActive,
        descriptionName: RFID_READING_TITLE,
        configurationName: "hasRfidReadingActive",
        isCheckinTime: false,
      },
      {
        id: 3,
        description: ATTENDANCE_DESCRIPTION,
        configuration: hasAttendanceActive,
        descriptionName: ATTENDANCE_TITLE,
        configurationName: "hasAttendanceActive",
        isCheckinTime: false,
      },
      {
        id: 4,
        description: CALL_TO_DOCK_DESCRIPTION,
        configuration: requestDockEntryActionActive,
        descriptionName: CALL_TO_DOCK_TITLE,
        configurationName: "requestDockEntryActionActive",
        isCheckinTime: false,
      },
      {
        id: 5,
        description: CALL_TO_LOBBY_DESCRIPTION,
        configuration: requestVehicleInboundActionActive,
        descriptionName: CALL_TO_LOBBY_TITLE,
        configurationName: "requestVehicleInboundActionActive",
        isCheckinTime: false,
      },
      {
        id: 6,
        description: "-",
        configuration: vehicleArrivalConfirmActionActive,
        descriptionName: ALLOW_VEHICLE_ARRIVAL_CONFIRM_TITLE,
        configurationName: "vehicleArrivalConfirmActionActive",
        isCheckinTime: false,
      },
      {
        id: 7,
        description: RELEASE_WMS_DOCUMENTS_DESCRIPTION,
        configuration: hasReleaseWMSDocumentsActive,
        descriptionName: RELEASE_WMS_DOCUMENTS_TITLE,
        configurationName: "hasReleaseWMSDocumentsActive",
        isCheckinTime: false,
      },
      {
        id: 8,
        description: EXTERNAL_DROP_HOOK_DESCRIPTION,
        configuration: dropHookActive,
        descriptionName: EXTERNAL_DROP_HOOK_TITLE,
        configurationName: "dropHookActive",
        isCheckinTime: false,
        hasDropHookOptions: true,
        dropHookType: dropHookTypeActive,
        disabled: false,
      },
      {
        id: 9,
        description: DRIVER_DOCUMENT_DESCRIPTION,
        configuration: true,
        descriptionName: DRIVER_DOCUMENT_TITLE,
        configurationName: "documentTypeActive",
        isCheckinTime: false,
        disabled: true,
        documentType: documentTypeActive,
        hasDocumentIdentification: true,
      },
      {
        id: 10,
        description: FINISH_OPERATION_DESCRIPTION,
        configuration: hasFinishOperationActionActive,
        descriptionName: FINISH_OPERATION_TITLE,
        configurationName: "hasFinishOperationActionActive",
        isCheckinTime: false,
      },
      {
        id: 11,
        description: LOCATION_OUT_OF_RANGE_DESCRIPTION,
        configuration: hasLocationOutOfRangeActive,
        descriptionName: LOCATION_OUT_OF_RANGE_TITLE,
        configurationName: "hasLocationOutOfRangeActive",
        isCheckinTime: false,
      },
      {
        id: 12,
        description: PLATE_FORMAT_DESCRIPTION,
        configuration: true,
        plateFormatType: plateFormatType,
        descriptionName: PLATE_FORMAT_DESCRIPTION,
        configurationName: "plateFormatType",
        isCheckinTime: false,
        hasPlateFormat: true,
        disabled: true,
      },
      {
        id: 13,
        description: COOL_FLEET_DESCRIPTION,
        configuration: hasEntryPermissionActive,
        descriptionName: COOL_FLEET_TITLE,
        configurationName: "hasEntryPermissionActive",
        isCheckinTime: false,
      },
      {
        id: 14,
        description: RELEASE_VEHICLE_DESCRIPTION,
        configuration: releaseVehicleActionActive,
        descriptionName: RELEASE_VEHICLE_TITLE,
        configurationName: "releaseVehicleActionActive",
        isCheckinTime: false,
      },
      {
        id: 15,
        description: EXPIRATION_TIME_DESCRIPTION,
        configuration: false,
        descriptionName: EXPIRATION_TIME_TITLE,
        configurationName: "expirationTimeActive",
        isCheckinTime: true,
        expirationTime: expirationTimeActive,
      },
      {
        id: 16,
        description: VEHICLE_IN_THE_EXTERNAL_YARD_DESCRIPTION,
        configuration: checkinActive,
        descriptionName: VEHICLE_IN_THE_EXTERNAL_YARD_TITLE,
        configurationName: "checkinActive",
        isCheckinTime: false,
      },
    ];
    return optionsConfigurations;
  }, [
    checkinActive,
    documentTypeActive,
    expirationTimeActive,
    requestVehicleInboundActionActive,
    requestDockEntryActionActive,
    hasFinishOperationActionActive,
    releaseVehicleActionActive,
    hasAttendanceActive,
    vehicleArrivalConfirmActionActive,
    hasReleaseWMSDocumentsActive,
    hasLocationOutOfRangeActive,
    hasCheckinScheduleActive,
    dropHookActive,
    dropHookTypeActive,
    hasEntryPermissionActive,
    hasRfidReadingActive,
    plateFormatType,
  ]);

  useEffect(() => {
    locationsHasAttendanceActive(hasAttendanceActive);
    locationsCheckinActive(checkinActive);
    locationsExpirationTimeActive(expirationTimeActive);
    locationsAllowRequestVehicleInboundActionActive(requestVehicleInboundActionActive);
    locationsAllowRequestDockEntryActionActive(requestDockEntryActionActive);
    locationsHasFinishOperationActionActive(hasFinishOperationActionActive);
    locationsReleaseVehicleActionActive(releaseVehicleActionActive);
    locationsAllowVehicleArrivalConfirmActionActive(vehicleArrivalConfirmActionActive);
    locationsHasReleaseWMSDocumentsActionActive(hasReleaseWMSDocumentsActive);
    locationsHasLocationOutOfRangeActive(hasLocationOutOfRangeActive);
    locationsHasCheckinScheduleActive(hasCheckinScheduleActive);
    locationsDocumentNumberActive(documentTypeActive);
    locationsDropHookActive(dropHookTypeActive);
    locationsHasEntryPermissionActive(hasEntryPermissionActive);
    locationsHasRfidReadingActive(hasRfidReadingActive);
    locationsPlateFormatActive(plateFormatType);
  }, [
    checkinActive,
    expirationTimeActive,
    requestVehicleInboundActionActive,
    requestDockEntryActionActive,
    hasFinishOperationActionActive,
    releaseVehicleActionActive,
    hasAttendanceActive,
    vehicleArrivalConfirmActionActive,
    hasReleaseWMSDocumentsActive,
    hasLocationOutOfRangeActive,
    hasCheckinScheduleActive,
    documentTypeActive,
    dropHookTypeActive,
    dropHookActive,
    hasEntryPermissionActive,
    hasRfidReadingActive,
    plateFormatType,
  ]);

  const onChangeConfiguration = (configuration) => {
    locationsConfigurationsUpdateConfigurationActive(configuration);
    locationsConfigurationsShowDrawer();
  };

  return (
    <>
      {configurationsWithTranslations.map((configuration) => (
        <>
          <S.ConfigurationsContainer key={configuration.id}>
            <S.HeaderConfiguration>
              <S.DescriptionConfiguration>{configuration.descriptionName}</S.DescriptionConfiguration>
              <span>{configuration.description}</span>
            </S.HeaderConfiguration>
            <div>
              {configuration.isCheckinTime ? (
                <Tag className="icon" color={theme.colors.loblolly}>
                  <div style={{ color: "#000" }}>{`${configuration?.expirationTime}h`}</div>
                </Tag>
              ) : (
                <Tag
                  className="icon"
                  style={{ color: configuration.configuration ? theme["@color-positive-600"] : theme["@color-warning-600"] }}
                  icon={configuration.configuration ? <Check /> : <Warning />}
                  color={configuration.configuration ? theme["@color-positive-100"] : theme["@color-warning-100"]}
                >
                  {configuration.configuration ? ACTIVE : INACTIVE}
                </Tag>
              )}
            </div>
            <S.ActionContainer>
              <Button
                type="text"
                shape="circle"
                icon={<MoreOutlined className="hoverActionButton" />}
                className="actionButton"
                data-testid={`moreOptions-${configuration?.configurationName}`}
                onClick={() => onChangeConfiguration(configuration)}
              />
            </S.ActionContainer>
          </S.ConfigurationsContainer>
          <Divider />
        </>
      ))}
    </>
  );
};

export default ConfigurationTabPane;
